import { Alert, Stack, Typography, Box } from '@mui/material'
import Container from '@mui/material/Container'
import { CreateCampaignHeader } from '~/features/campaign_create/Header'
import { CreateCampaignContext } from '~/contexts/CreateCampaignContext'
import React, { FC, useContext } from 'react'
import { CampaignCategory, CampaignCostHandlingMethod, CampaignScheduleType, SearchGenre } from '~/generated/graphql'
import {
  SponsorServiceName,
  sponsorServiceNameSchemaFields,
  validateCampaignNameLength,
} from '~/features/campaign_input/SponsorServiceName'
import {
  SponsorServicePrice,
  sponsorServicePriceSchemaFields,
  validateSponsorServicePrice,
} from '~/features/campaign_input/SponsorServicePrice'
import { SponsorServiceUrl, sponsorServiceUrlSchemaFields } from '~/features/campaign_input/SponsorServiceUrl'
import { NavigationButtons } from '../NavigationButtons'
import { useCampaignPageStepForm } from '../useCampaignPageStepForm'
import { prTargetAgeSchemaFields } from '~/features/campaign_input/PrTargetAge'
import { prTargetGenderSchemaFields } from '~/features/campaign_input/PrTargetGender'
import { CampaignImage, campaignImageSchemaFields } from '~/features/campaign_input/CampaignImage'
import { FormProvider } from 'react-hook-form'
import { z } from 'zod'
import { maxCompanionCountSchemaFields, MaxCompanionCount } from '../../campaign_input/MaxCompanionCount'
import {
  GenreAndSubGenre,
  genreAndSubGenreSchemaFields,
  validateSearchGenre,
} from '../../campaign_input/GenreAndSubGenre'
import { PrTarget } from '../../campaign_input/PrTarget'
import { LinkToInfo } from '~/components/common/LinkToInfo'
import InfoIcon from '@mui/icons-material/Info'
import { CampaignPostFooter } from '~/components/CampaignPostFooter'
import { selectCostHandlingMethodSchemaFields } from '../../campaign_input/SelectCostHandlingMethod'
import { EXTERNAL_LINKS } from '~/lib/externalLinks'

const schema = z

  .object({
    ...sponsorServiceNameSchemaFields,
    ...sponsorServicePriceSchemaFields,
    ...sponsorServiceUrlSchemaFields,
    ...campaignImageSchemaFields,
    ...prTargetAgeSchemaFields,
    ...prTargetGenderSchemaFields,
    ...maxCompanionCountSchemaFields,
    // Note:genreは他のschemaFieldsに上書きされる可能性があるので最後に配置する
    ...genreAndSubGenreSchemaFields,
    ...selectCostHandlingMethodSchemaFields,
  })
  .superRefine(validateSponsorServicePrice)
  .superRefine(validateCampaignNameLength)
  .superRefine(validateSearchGenre)

export const ServiceContents: FC = () => {
  const { campaign, setCampaign } = useContext(CreateCampaignContext)
  const category = campaign.category ?? CampaignCategory.Product
  const scheduleType = ({
    category,
    genre,
    costHandlingMethod,
  }: {
    category: CampaignCategory
    genre: SearchGenre | ''
    costHandlingMethod: CampaignCostHandlingMethod
  }) => {
    if (costHandlingMethod === CampaignCostHandlingMethod.ExpenseCampaign) {
      return CampaignScheduleType.NotUsed
    }
    if (
      category === CampaignCategory.Product ||
      (category === CampaignCategory.Service && genre === SearchGenre.Travel)
    ) {
      return CampaignScheduleType.NotUsed
    }

    return CampaignScheduleType.ReservableSchedule
  }
  const { onSubmit, onBack, useFormMethods } = useCampaignPageStepForm<z.input<typeof schema>, z.output<typeof schema>>(
    {
      defaultValues: {
        name: campaign.name,
        searchGenre: campaign.searchGenre,
        searchSubGenreIds: campaign.searchSubGenreIds,
        sponsorServicePriceIntax: campaign.sponsorServicePriceIntax,
        url: campaign.url,
        coverImages: campaign.coverImages,
        prTargetGender: campaign.prTargetGender,
        prTargetAge: campaign.prTargetAge,
        maxCompanionCount: campaign.maxCompanionCount,
        category: category,
        costHandlingMethod: campaign.costHandlingMethod,
      },
      schema,
      commit: (data: z.output<typeof schema>) => {
        setCampaign((campaign) => {
          return {
            ...campaign,
            ...data,
            scheduleType: scheduleType({
              category: data.category,
              genre: data.searchGenre,
              costHandlingMethod: data.costHandlingMethod,
            }),
          }
        })
      },
    },
  )
  const { watch } = useFormMethods
  const costHandlingMethod = watch('costHandlingMethod')

  return (
    <form onSubmit={onSubmit}>
      <CreateCampaignHeader title="PRする商材に関する情報について教えてください" onBack={onBack} />
      <Container sx={{ display: 'flex', flexDirection: 'column' }} maxWidth="sm">
        <Stack gap={5}>
          <FormProvider {...useFormMethods}>
            {/* 商材の名称 */}
            <SponsorServiceName
              tooltip={
                <>
                  <Stack gap={1}>
                    <Typography variant="body2">キャンペーン名としてインフルエンサーに表示される項目です。</Typography>
                    <Typography variant="body2">商品 / サービスの名称のみを記載してください。</Typography>
                  </Stack>
                  <Box mt={2}>
                    <Typography variant="body2">記入例：</Typography>
                    <CampaignPostFooter>
                      {category === CampaignCategory.Product ? (
                        <Stack gap={2}>
                          <Typography variant="body2">・《冷凍》チーズケーキ（5号）</Typography>
                          <Typography variant="body2">・国産和牛（300g）</Typography>
                          <Typography variant="body2">・栗饅頭 8個入り</Typography>
                          <Typography variant="body2">・Tシャツ 4種の中から1つ</Typography>
                          <Typography variant="body2">・オードトワレ（50mL）［type01：ローズ］</Typography>
                        </Stack>
                      ) : (
                        <Stack gap={2}>
                          <Typography variant="body2">・海鮮コース全 10品 2時間飲み放題付き</Typography>
                          <Typography variant="body2">・チョコケーキ＋お好きなセットドリンク1杯</Typography>
                          <Typography variant="body2">・整体エステコース 90分</Typography>
                          <Typography variant="body2">・スタンダード洋室プラン 1泊 夕朝食付き</Typography>
                        </Stack>
                      )}
                    </CampaignPostFooter>
                  </Box>
                </>
              }
            />

            {/* ジャンルとサブジャンル */}
            <GenreAndSubGenre />

            {/* 商材の定価（税込） */}
            <SponsorServicePrice
              tooltip={
                costHandlingMethod === CampaignCostHandlingMethod.ExpenseCampaign ? (
                  campaign.category === CampaignCategory.Service ? (
                    <SponsorServicePriceExpenseServiceTooltip />
                  ) : (
                    <SponsorServicePriceExpenseProductTooltip />
                  )
                ) : (
                  <SponsorServicePriceNoChargeCampaignTooltip />
                )
              }
            />

            {/* 商材の内容が確認できるURL */}
            <SponsorServiceUrl
              costHandlingMethod={costHandlingMethod}
              tooltip={
                costHandlingMethod === CampaignCostHandlingMethod.ExpenseCampaign ? (
                  <SponsorServiceUrlExpenseTooltip />
                ) : (
                  <SponsorServiceUrlNoChargeCampaignTooltip />
                )
              }
            />

            {/* キャンペーン画像 */}
            <CampaignImage
              tooltip={
                <Stack gap={1}>
                  <LinkToInfo
                    href={EXTERNAL_LINKS.CAMPAIGN_IMAGE_EXAMPLES}
                    textBefore=""
                    linkText="画像例"
                    textAfter="を参考に設定してください。"
                    variant="body2"
                  />
                  <Typography variant="body2">
                    PRするサービス以外が写っている、使用許可が得られていない画像はお控えください。
                  </Typography>
                </Stack>
              }
            />

            {/* 商材のターゲット*/}
            <PrTarget
              description={
                <Alert severity="info" icon={<InfoIcon />} sx={{ py: 1, mb: 2 }}>
                  商材のターゲットを設定しておくと、マッチする属性のインフルエンサーが集まりやすくなります。
                  <br />
                  スムーズな採用につながるので、事前の設定がおすすめです！
                </Alert>
              }
            />

            {/* 同サービスを無償提供する同伴者の最大数 */}
            <MaxCompanionCount
              tooltip={
                <Stack gap={2}>
                  <Typography variant="body2">
                    インフルエンサーは、無償対象となる同伴者数の範囲内で人数を選択し、応募します。
                    <br />
                    同伴者にはインフルエンサーと同じサービスを無償提供する必要があります。
                  </Typography>
                  <Stack gap={0.5}>
                    <LinkToInfo
                      href={EXTERNAL_LINKS.COMPANION_INFO}
                      linkText="こちら"
                      variant="body2"
                    />
                    <LinkToInfo
                      textBefore="同伴者についてのFAQは"
                      href={EXTERNAL_LINKS.COMPANION_FAQ}
                      variant="body2"
                    />
                  </Stack>
                </Stack>
              }
            />
          </FormProvider>
        </Stack>

        <NavigationButtons />
      </Container>
    </form>
  )
}

const SponsorServicePriceNoChargeCampaignTooltip: FC = () => {
  return (
    <>
      <Typography variant="body2">商材の定価が0円の場合は「0」と入力してください。</Typography>
      <Box mt={2}>
        <Typography variant="body2">例：</Typography>
        <CampaignPostFooter>
          <Stack gap={1}>
            <Typography variant="body2">・ アプリのPR</Typography>
            <Typography variant="body2">・ 提供商品なし</Typography>
            <Typography variant="body2">・ イベント参加</Typography>
          </Stack>
        </CampaignPostFooter>
      </Box>
    </>
  )
}

const SponsorServicePriceExpenseServiceTooltip: FC = () => {
  return (
    <>
      <Typography variant="body2">
        こちらの項目には、対象サービスを実際に注文・利用した場合にかかる税込の金額をご入力ください。
      </Typography>
      <Typography variant="body2" mt={1}>
        （例：コース料理をPRする場合 → そのコースを1人分注文した際にかかる税込価格）
      </Typography>

      <Box mt={2}>
        <Typography variant="body2" fontWeight="bold">
          例
        </Typography>
        <CampaignPostFooter>
          <Stack gap={1}>
            <Typography variant="body2">サービスの税込金額：5,000円</Typography>
            <Typography variant="body2">サービス提供人数（インフルエンサー1名＋同伴者1名）：2名</Typography>
            <Typography variant="body2">→ インフルエンサーの立替申請額の上限：5,000円 × 2名 = 10,000円</Typography>
            <Typography variant="body2">※広告主様のご負担額は、実際の立替申請額に還元率を適用した金額となります。</Typography>
          </Stack>
        </CampaignPostFooter>
      </Box>
    </>
  )
}

const SponsorServicePriceExpenseProductTooltip: FC = () => {
  return (
    <>
      <Typography variant="body2">
        こちらの項目には、実際に商品を購入する際にかかる税込の総額をご入力ください。
      </Typography>
      <Typography variant="body2" mt={1}>
        この金額には、商品代金のほか、送料・各種手数料（離島手数料・代引き手数料など）も含めてご記載ください。
      </Typography>
      <Typography variant="body2" mt={1}>
        ご入力いただいた金額は、インフルエンサーの立替申請額の上限（商品購入1件あたり）として使用いたします。
      </Typography>

      <Box mt={2}>
        <Typography variant="body2" fontWeight="bold">
          広告主様のご負担額について
        </Typography>
        <Typography variant="body2" mt={1}>
          インフルエンサーが商品購入時に実際に支払った金額（実費）に、立替費用還元率を適用した金額が広告主様のご負担額となります。
        </Typography>
        <Typography variant="body2" mt={1}>
          そのため、必ずしも本項目にご入力いただいた上限額の満額が広告主様の負担額となるわけではございません。
        </Typography>
      </Box>
    </>
  )
}

const SponsorServiceUrlExpenseTooltip: FC = () => {
  return (
    <>
      <Stack gap={1}>
        <Typography variant="body2">
          実際に体験いただくサービス内容が明確にわかる、公式サイトや予約ページ、メニュー詳細ページなどのURLをご入力ください。
        </Typography>

        <Box mt={2}>
          <Typography variant="body2">例えば以下のようなURLが該当します：</Typography>
          <CampaignPostFooter>
            <Stack gap={1}>
              <Typography variant="body2">・ コース料理や施術内容が記載されたプラン詳細ページ</Typography>
              <Typography variant="body2">・ 実際に購入する商品の販売ページ</Typography>
              <Typography variant="body2">・ サービスの金額・内容・提供場所が確認できる予約サイトのURL など</Typography>
            </Stack>
          </CampaignPostFooter>
        </Box>

        <Box mt={2}>
          <Typography variant="body2" fontWeight="bold">
            ※入力いただいたURLをもとに、サービス内容の確認および立替負担金額（広告主負担分）の妥当性を確認いたします。
          </Typography>
          <Typography variant="body2" mt={1}>
            ※URLは体験予定のサービスに直接該当するページをご指定ください。
          </Typography>
        </Box>
      </Stack>
    </>
  )
}

const SponsorServiceUrlNoChargeCampaignTooltip: FC = () => {
  return (
    <>
      <Stack gap={1}>
        <Typography variant="body2">インフルエンサーはこちらのURLを参考に応募し、PR投稿を作成します。</Typography>
        <Typography variant="body2">
          商材の内容・価格が記載されていれば、Instagramの投稿URL等でも構いません。
        </Typography>
      </Stack>
    </>
  )
}
